/**
 * Notification Store
 *
 * Manages notification list, unread count, panel/toast UI state,
 * and handles real-time WebSocket events for notifications.
 */

import { create } from "zustand";
import { devtools } from "zustand/middleware";
import type { NotificationCreatedData } from "@/types/events.types";
import { logger } from "@/services/logger";
import { generateUUID } from "@/utils/uuid";

// ============================================================
// Types
// ============================================================

export interface AppNotification {
  id: string;
  type: string;
  title: string;
  message: string;
  severity: "info" | "success" | "warning" | "error";
  actionUrl?: string;
  actionLabel?: string;
  metadata?: Record<string, unknown>;
  isRead: boolean;
  createdAt: Date;
}

interface NotificationState {
  notifications: AppNotification[];
  unreadCount: number;
  isLoading: boolean;
  hasMore: boolean;
  isPanelOpen: boolean;
  pendingToast: AppNotification | null;
}

interface NotificationStore extends NotificationState {
  // Data actions
  setNotifications: (notifications: AppNotification[]) => void;
  appendNotifications: (notifications: AppNotification[]) => void;
  setUnreadCount: (count: number) => void;
  incrementUnreadCount: () => void;
  decrementUnreadCount: () => void;
  setLoading: (loading: boolean) => void;
  setHasMore: (hasMore: boolean) => void;

  // Panel
  openPanel: () => void;
  closePanel: () => void;
  togglePanel: () => void;

  // Toast
  setPendingToast: (notification: AppNotification | null) => void;
  clearPendingToast: () => void;

  // WebSocket event handlers
  handleNotificationCreated: (data: NotificationCreatedData) => void;
  handleNotificationRead: (notificationId: string) => void;
  handleNotificationBatchRead: (data: { markedCount: number }) => void;
  handleNotificationDeleted: (notificationId: string) => void;

  // Optimistic local actions
  markAsRead: (notificationId: string) => void;
  markAllAsRead: () => void;
  deleteNotification: (notificationId: string) => void;
}

// ============================================================
// Initial State
// ============================================================

const initialState: NotificationState = {
  notifications: [],
  unreadCount: 0,
  isLoading: false,
  hasMore: true,
  isPanelOpen: false,
  pendingToast: null,
};

// ============================================================
// Store
// ============================================================

export const useNotificationStore = create<NotificationStore>()(
  devtools(
    (set, get) => ({
      ...initialState,

      // Data actions
      setNotifications: (notifications) => {
        set({ notifications }, false, "setNotifications");
      },

      appendNotifications: (newNotifications) => {
        const { notifications } = get();
        const existingIds = new Set(notifications.map((n) => n.id));
        const unique = newNotifications.filter((n) => !existingIds.has(n.id));
        set(
          { notifications: [...notifications, ...unique] },
          false,
          "appendNotifications",
        );
      },

      setUnreadCount: (count) => {
        set({ unreadCount: count }, false, "setUnreadCount");
      },

      incrementUnreadCount: () => {
        const { unreadCount } = get();
        set({ unreadCount: unreadCount + 1 }, false, "incrementUnreadCount");
      },

      decrementUnreadCount: () => {
        const { unreadCount } = get();
        set(
          { unreadCount: Math.max(0, unreadCount - 1) },
          false,
          "decrementUnreadCount",
        );
      },

      setLoading: (isLoading) => {
        set({ isLoading }, false, "setLoading");
      },

      setHasMore: (hasMore) => {
        set({ hasMore }, false, "setHasMore");
      },

      // Panel
      openPanel: () => {
        set({ isPanelOpen: true }, false, "openPanel");
      },

      closePanel: () => {
        set({ isPanelOpen: false }, false, "closePanel");
      },

      togglePanel: () => {
        const { isPanelOpen } = get();
        set({ isPanelOpen: !isPanelOpen }, false, "togglePanel");
      },

      // Toast
      setPendingToast: (notification) => {
        set({ pendingToast: notification }, false, "setPendingToast");
      },

      clearPendingToast: () => {
        set({ pendingToast: null }, false, "clearPendingToast");
      },

      // WebSocket event handlers
      handleNotificationCreated: (data) => {
        const correlationId = generateUUID();
        logger.info("Notification received via WebSocket", {
          correlationId,
          notificationId: data.notificationId,
          type: data.type,
        });

        const notification: AppNotification = {
          id: data.notificationId,
          type: data.type,
          title: data.title,
          message: data.message,
          severity: data.severity,
          actionUrl: data.actionUrl,
          actionLabel: data.actionLabel,
          metadata: data.metadata,
          isRead: false,
          createdAt: new Date(data.createdAt),
        };

        const { notifications } = get();
        set(
          {
            notifications: [notification, ...notifications],
            pendingToast: notification,
          },
          false,
          "handleNotificationCreated",
        );
      },

      handleNotificationRead: (notificationId) => {
        const { notifications } = get();
        set(
          {
            notifications: notifications.map((n) =>
              n.id === notificationId ? { ...n, isRead: true } : n,
            ),
          },
          false,
          "handleNotificationRead",
        );
      },

      handleNotificationBatchRead: (_data) => {
        const { notifications } = get();
        set(
          {
            notifications: notifications.map((n) => ({ ...n, isRead: true })),
            unreadCount: 0,
          },
          false,
          "handleNotificationBatchRead",
        );
      },

      handleNotificationDeleted: (notificationId) => {
        const { notifications, unreadCount } = get();
        const target = notifications.find((n) => n.id === notificationId);
        const wasUnread = target && !target.isRead;
        set(
          {
            notifications: notifications.filter((n) => n.id !== notificationId),
            unreadCount: wasUnread ? Math.max(0, unreadCount - 1) : unreadCount,
          },
          false,
          "handleNotificationDeleted",
        );
      },

      // Optimistic local actions
      markAsRead: (notificationId) => {
        const { notifications, unreadCount } = get();
        const target = notifications.find((n) => n.id === notificationId);
        if (target && !target.isRead) {
          set(
            {
              notifications: notifications.map((n) =>
                n.id === notificationId ? { ...n, isRead: true } : n,
              ),
              unreadCount: Math.max(0, unreadCount - 1),
            },
            false,
            "markAsRead",
          );
        }
      },

      markAllAsRead: () => {
        const { notifications } = get();
        set(
          {
            notifications: notifications.map((n) => ({ ...n, isRead: true })),
            unreadCount: 0,
          },
          false,
          "markAllAsRead",
        );
      },

      deleteNotification: (notificationId) => {
        const { notifications, unreadCount } = get();
        const target = notifications.find((n) => n.id === notificationId);
        const wasUnread = target && !target.isRead;
        set(
          {
            notifications: notifications.filter((n) => n.id !== notificationId),
            unreadCount: wasUnread ? Math.max(0, unreadCount - 1) : unreadCount,
          },
          false,
          "deleteNotification",
        );
      },
    }),
    {
      name: "notification-store",
      enabled: import.meta.env.DEV,
    },
  ),
);
